import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ColorPaletteTab = ({ config, onUpdate }) => {
  const [contrastResults, setContrastResults] = useState({});

  const colorSections = [
    {
      id: 'primary',
      title: 'Primary Colors',
      description: 'Main brand colors for primary actions and branding',
      colors: [
        { key: 'primary', label: 'Primary', value: config?.primary || '#2D9B9B' },
        { key: 'primaryLight', label: 'Primary Light', value: config?.primaryLight || '#4DAEAE' },
        { key: 'primaryDark', label: 'Primary Dark', value: config?.primaryDark || '#1A7A7A' }
      ]
    },
    {
      id: 'functional',
      title: 'Functional Colors',
      description: 'Colors for different states and functions',
      colors: [
        { key: 'secondary', label: 'Secondary', value: config?.secondary || '#1A7A7A' },
        { key: 'accent', label: 'Accent', value: config?.accent || '#3DB8B8' },
        { key: 'success', label: 'Success', value: config?.success || '#059669' },
        { key: 'warning', label: 'Warning', value: config?.warning || '#d97706' },
        { key: 'error', label: 'Error', value: config?.error || '#dc2626' }
      ]
    }
  ];

  const neutralColors = [
    { key: '50', label: 'Neutral 50', value: config?.neutral?.['50'] || '#f8fafc' },
    { key: '100', label: 'Neutral 100', value: config?.neutral?.['100'] || '#f1f5f9' },
    { key: '200', label: 'Neutral 200', value: config?.neutral?.['200'] || '#e2e8f0' },
    { key: '300', label: 'Neutral 300', value: config?.neutral?.['300'] || '#cbd5e1' },
    { key: '400', label: 'Neutral 400', value: config?.neutral?.['400'] || '#94a3b8' },
    { key: '500', label: 'Neutral 500', value: config?.neutral?.['500'] || '#64748b' },
    { key: '600', label: 'Neutral 600', value: config?.neutral?.['600'] || '#475569' },
    { key: '700', label: 'Neutral 700', value: config?.neutral?.['700'] || '#334155' },
    { key: '800', label: 'Neutral 800', value: config?.neutral?.['800'] || '#1e293b' },
    { key: '900', label: 'Neutral 900', value: config?.neutral?.['900'] || '#0f172a' }
  ];

  const handleColorChange = (colorKey, value) => {
    if (neutralColors.some(nc => nc.key === colorKey)) {
      onUpdate({
        neutral: {
          ...config?.neutral,
          [colorKey]: value
        }
      });
    } else {
      onUpdate({ [colorKey]: value });
    }
  };

  const calculateContrast = (color1, color2) => {
    // Simple contrast calculation (for demo purposes)
    const getLuminance = (color) => {
      const rgb = parseInt(color.replace('#', ''), 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = (rgb >> 0) & 0xff;
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    };

    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
  };

  const getContrastRating = (ratio) => {
    if (ratio >= 7) return { level: 'AAA', color: 'text-success' };
    if (ratio >= 4.5) return { level: 'AA', color: 'text-warning' };
    return { level: 'Fail', color: 'text-error' };
  };

  const checkContrast = (foreground, background) => {
    const ratio = calculateContrast(foreground, background);
    return getContrastRating(ratio);
  };

  const generateColorVariations = (baseColor) => {
    // Generate lighter and darker variations
    const variations = [];
    for (let i = 0; i < 5; i++) {
      const opacity = (i + 1) * 0.2;
      variations.push({
        opacity: opacity,
        color: baseColor,
        hex: baseColor // In a real implementation, you'd generate actual variations
      });
    }
    return variations;
  };

  return (
    <div className="space-y-8">
      {/* Color Sections */}
      {colorSections.map((section) => (
        <div key={section.id}>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center">
              <Icon name="Palette" size={20} className="mr-2 text-[#2D9B9B]" />
              {section.title}
            </h3>
            <p className="text-sm text-text-secondary">{section.description}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {section.colors.map((color) => (
              <div key={color.key} className="bg-surface border border-secondary-200 rounded-xl p-4">
                <div 
                  className="w-full h-20 rounded-lg mb-4 border-2 border-secondary-200 shadow-sm"
                  style={{ backgroundColor: color.value }}
                />
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      {color.label}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={color.value}
                        onChange={(e) => handleColorChange(color.key, e.target.value)}
                        className="w-12 h-8 rounded border border-secondary-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={color.value}
                        onChange={(e) => handleColorChange(color.key, e.target.value)}
                        className="flex-1 px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] text-sm font-mono"
                      />
                    </div>
                  </div>
                  
                  {/* Color Variations */}
                  <div>
                    <p className="text-xs text-text-secondary mb-2">Variations</p>
                    <div className="flex space-x-1">
                      {generateColorVariations(color.value).map((variation, index) => (
                        <div
                          key={index}
                          className="w-6 h-6 rounded border border-secondary-200"
                          style={{ 
                            backgroundColor: variation.color,
                            opacity: variation.opacity
                          }}
                          title={`${Math.round(variation.opacity * 100)}% opacity`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Neutral Colors */}
      <div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center">
            <Icon name="Layers" size={20} className="mr-2 text-[#2D9B9B]" />
            Neutral Colors
          </h3>
          <p className="text-sm text-text-secondary">
            Neutral colors for backgrounds, borders, and text
          </p>
        </div>
        
        <div className="bg-surface border border-secondary-200 rounded-xl p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-4">
            {neutralColors.map((color) => (
              <div key={color.key} className="text-center">
                <div 
                  className="w-full h-16 rounded-lg mb-2 border-2 border-secondary-200 shadow-sm"
                  style={{ backgroundColor: color.value }}
                />
                <div className="space-y-1">
                  <p className="text-xs font-medium text-text-primary">{color.label}</p>
                  <input
                    type="text"
                    value={color.value}
                    onChange={(e) => handleColorChange(color.key, e.target.value)}
                    className="w-full px-2 py-1 border border-secondary-200 rounded text-xs font-mono text-center focus:ring-1 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Accessibility & Contrast */}
      <div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center">
            <Icon name="Eye" size={20} className="mr-2 text-[#2D9B9B]" />
            Accessibility & Contrast
          </h3>
          <p className="text-sm text-text-secondary">
            Ensure your colors meet WCAG accessibility standards
          </p>
        </div>

        <div className="bg-surface border border-secondary-200 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contrast Checker */}
            <div>
              <h4 className="font-medium text-text-primary mb-4">Contrast Checker</h4>
              <div className="space-y-4">
                {[
                  { bg: config?.primary || '#2D9B9B', fg: '#ffffff', label: 'Primary + White' },
                  { bg: config?.secondary || '#1A7A7A', fg: '#ffffff', label: 'Secondary + White' },
                  { bg: config?.success || '#059669', fg: '#ffffff', label: 'Success + White' },
                  { bg: config?.warning || '#d97706', fg: '#ffffff', label: 'Warning + White' },
                  { bg: config?.error || '#dc2626', fg: '#ffffff', label: 'Error + White' }
                ].map((combo, index) => {
                  const contrast = checkContrast(combo.fg, combo.bg);
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="flex">
                          <div 
                            className="w-8 h-8 rounded-l-md border border-secondary-200"
                            style={{ backgroundColor: combo.bg }}
                          />
                          <div 
                            className="w-8 h-8 rounded-r-md border border-secondary-200"
                            style={{ backgroundColor: combo.fg }}
                          />
                        </div>
                        <span className="text-sm text-text-primary">{combo.label}</span>
                      </div>
                      <span className={`text-sm font-medium ${contrast.color}`}>
                        {contrast.level}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Color Harmony */}
            <div>
              <h4 className="font-medium text-text-primary mb-4">Color Harmony</h4>
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] rounded-lg">
                  <p className="text-white text-sm font-medium">Primary Gradient</p>
                  <p className="text-white/80 text-xs">Main brand gradient</p>
                </div>
                <div className="p-4 bg-gradient-to-r from-[#1A7A7A] to-[#3DB8B8] rounded-lg">
                  <p className="text-white text-sm font-medium">Secondary Gradient</p>
                  <p className="text-white/80 text-xs">Supporting gradient</p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[config?.primary, config?.secondary, config?.accent, config?.success].map((color, index) => (
                    <div
                      key={index}
                      className="aspect-square rounded-lg border-2 border-secondary-200"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Color Usage Guidelines */}
      <div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center">
            <Icon name="BookOpen" size={20} className="mr-2 text-[#2D9B9B]" />
            Usage Guidelines
          </h3>
        </div>

        <div className="bg-gradient-to-r from-[#2D9B9B]/5 to-[#1A7A7A]/5 border border-[#2D9B9B]/20 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-text-primary mb-3 flex items-center">
                <div className="w-4 h-4 bg-[#2D9B9B] rounded mr-2" />
                Primary Colors
              </h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Main CTAs and buttons</li>
                <li>• Navigation highlights</li>
                <li>• Important links</li>
                <li>• Brand elements</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-text-primary mb-3 flex items-center">
                <div className="w-4 h-4 bg-[#059669] rounded mr-2" />
                Status Colors
              </h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Success: Confirmations</li>
                <li>• Warning: Cautionary messages</li>
                <li>• Error: Error states</li>
                <li>• Info: Informational content</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-text-primary mb-3 flex items-center">
                <div className="w-4 h-4 bg-secondary-500 rounded mr-2" />
                Neutral Colors
              </h4>
              <ul className="space-y-2 text-text-secondary">
                <li>• Text content</li>
                <li>• Backgrounds</li>
                <li>• Borders and dividers</li>
                <li>• Subtle UI elements</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPaletteTab;